import type { HillshadeLayerSpecification, Map as GeographicMap, StyleSpecification } from "maplibre-gl";
import { TERRAIN_CAPTURE_ID, TERRAIN_MASK_ID } from "./municipality-terrain-mask";

// Mapzen's global AWS Open Data dataset, not a demo server. Provider documentation
// and regional source metadata are recorded in e2e/fixtures/README.md.
export const DEM_TILE_URL = "https://elevation-tiles-prod.s3.amazonaws.com/terrarium/{z}/{x}/{y}.png";
export const DEM_SOURCE_ID = "rbim-elevation-dem";
export const HILLSHADE_LAYER_ID = "rbim-elevation-hillshade";
export const TERRAIN_CREDITS_URL = "https://github.com/tilezen/joerd/blob/master/docs/attribution.md";
export const TERRAIN_ATTRIBUTION = `<a href="${TERRAIN_CREDITS_URL}">Mapzen Terrain Tiles</a> · SRTM/GMTED2010 data courtesy of the U.S. Geological Survey · ETOPO1: NOAA`;

export const HILLSHADE_PAINT: HillshadeLayerSpecification["paint"] = {
  "hillshade-method": "standard",
  "hillshade-illumination-anchor": "map",
  "hillshade-illumination-direction": 315,
  "hillshade-illumination-altitude": 40,
  // Standard hillshade's supported intensity tops out at 1. Preserve the
  // natural palette while making slopes legible beneath the population fills.
  "hillshade-exaggeration": 1,
  "hillshade-shadow-color": "#34433d",
  "hillshade-highlight-color": "#fff9ed",
  "hillshade-accent-color": "#566456",
};

/** Compose before setStyle: no load-event races or addSource/addLayer duplication.
 * Liberty's landcover/water precede aeroways/transportation. Keep those routes,
 * buildings and labels above relief, with the existing RBIM SVG above the canvas.
 * Removing our reserved layer first makes repeated style composition idempotent.
 */
export function withTerrainRelief(style: StyleSpecification): StyleSpecification {
  const layers = style.layers.filter((layer) => layer.id !== HILLSHADE_LAYER_ID);
  const insertion = layers.findIndex((layer) =>
    ("source-layer" in layer && ["aeroway", "transportation", "building"].includes(layer["source-layer"] ?? "")) ||
    layer.type === "symbol",
  );
  layers.splice(insertion < 0 ? layers.length : insertion, 0, {
    id: HILLSHADE_LAYER_ID, type: "hillshade", source: DEM_SOURCE_ID,
    paint: { ...HILLSHADE_PAINT },
  });
  return {
    ...style,
    sources: { ...style.sources, [DEM_SOURCE_ID]: {
      type: "raster-dem", tiles: [DEM_TILE_URL], tileSize: 256,
      encoding: "terrarium", minzoom: 0, maxzoom: 15,
      attribution: TERRAIN_ATTRIBUTION,
    } },
    layers,
  };
}

export function isTerrainError(event: { sourceId?: string; error?: unknown }) {
  const url = event.error && typeof event.error === "object" && "url" in event.error ? event.error.url : undefined;
  return event.sourceId === DEM_SOURCE_ID ||
    (typeof url === "string" && url.startsWith("https://elevation-tiles-prod.s3.amazonaws.com/"));
}

/** Disable only the optional enhancement, cancelling pending DEM requests. */
export function removeTerrainRelief(map: Pick<GeographicMap, "getLayer" | "removeLayer" | "getSource" | "removeSource">) {
  if (map.getLayer(TERRAIN_MASK_ID)) map.removeLayer(TERRAIN_MASK_ID);
  if (map.getLayer(TERRAIN_CAPTURE_ID)) map.removeLayer(TERRAIN_CAPTURE_ID);
  if (map.getLayer(HILLSHADE_LAYER_ID)) map.removeLayer(HILLSHADE_LAYER_ID);
  if (map.getSource(DEM_SOURCE_ID)) map.removeSource(DEM_SOURCE_ID);
}
